import type { z } from 'zod'
import {
	bindPersistedToUser,
	boundUser,
	forgetEntry,
	hydratePersisted,
	persistEntry,
	takeHydrated,
} from './persist.ts'

// The shared read cache behind useResource.
//
// It lives at module level rather than in the hook, so data outlives the
// component that fetched it: App.tsx swaps whole mini-apps by route, and
// without this, todo → activities → todo refetches from nothing every time.
// Entries listed in persist.ts are also written to IndexedDB, so the same is
// true across a page load.
//
// Keyed by the resource key useResource already serialises. A key therefore
// has to identify the request — two call sites sharing a key are asking for
// the same thing, and will be served the same response.

const entries = new Map<string, unknown>()

// Bumped by every local edit, and paired with `epoch` into the token a fetch
// snapshots before it starts. If the token has moved by the time the response
// lands, the response lost a race to an optimistic update (or to a sign-out)
// and is dropped rather than written.
const generations = new Map<string, number>()
let epoch = 0

// Keys with a write in progress. A fetch that starts mid-write can answer
// with the data from before it, so fetches for a held key are skipped.
const holds = new Map<string, number>()

export const isHeld = (key: string): boolean => (holds.get(key) ?? 0) > 0

// Returns the release. Holds count, so overlapping writes each keep their own.
export const holdKey = (key: string): (() => void) => {
	holds.set(key, (holds.get(key) ?? 0) + 1)
	let released = false
	return () => {
		if (released) return
		released = true
		const n = (holds.get(key) ?? 1) - 1
		if (n > 0) holds.set(key, n)
		else holds.delete(key)
	}
}

type Inflight = { token: string; promise: Promise<unknown> }
const inflight = new Map<string, Inflight>()

// A schema means the key is persisted, and the first read of it this session
// may have to come off disk and be parsed before it can be trusted.
export const readCache = (key: string, schema?: z.ZodType): unknown => {
	const live = entries.get(key)
	if (live !== undefined) return live
	if (!schema) return undefined

	const raw = takeHydrated(key)
	if (raw === undefined) return undefined

	const parsed = schema.safeParse(raw)
	if (!parsed.success) {
		// Written by an older app version, most likely. Drop it and let the
		// network answer.
		forgetEntry(key)
		return undefined
	}
	entries.set(key, parsed.data)
	return parsed.data
}

export const writeCache = (key: string, value: unknown, persist: boolean) => {
	if (value === undefined) {
		entries.delete(key)
		if (persist) forgetEntry(key)
		return
	}
	entries.set(key, value)
	if (persist) persistEntry(key, value)
}

export const tokenFor = (key: string): string =>
	`${epoch}:${generations.get(key) ?? 0}`

export const bumpGeneration = (key: string) => {
	generations.set(key, (generations.get(key) ?? 0) + 1)
}

// One request per key in flight at a time. `['recipes']` is fetched by both
// Recipes and Meals, `['household-members']` by both Todo and Settings, and
// today mounting either pair fires two identical requests.
//
// A request that started at an older token isn't joined: it would answer with
// data from before the edit that moved the token.
export const fetchOnce = (
	key: string,
	fetcher: () => Promise<unknown>,
): Promise<unknown> => {
	const token = tokenFor(key)
	const existing = inflight.get(key)
	if (existing?.token === token) return existing.promise

	const promise = fetcher().finally(() => {
		if (inflight.get(key)?.promise === promise) inflight.delete(key)
	})
	inflight.set(key, { token, promise })
	return promise
}

// Bumping the epoch invalidates every in-flight fetch, including one that has
// already resolved and is waiting to check its token.
const clearMemory = () => {
	epoch += 1
	entries.clear()
	generations.clear()
	inflight.clear()
}

export const hydrateCache = (userId: string | null) => hydratePersisted(userId)

// Who the cache belongs to. One account must never be shown the previous
// one's content, so a change of user — including a sign-out — empties both
// the memory and the disk.
export const bindCacheToUser = (userId: string | null) => {
	if (userId === boundUser()) return
	clearMemory()
	bindPersistedToUser(userId)
}
